import { useEffect, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
} from '@mui/material';

export const PLATFORMS = [
    { key: 'AMAZON', name: 'Amazon DSP' },
    { key: 'DV360', name: 'Google DV360' },
] as const;

export type PlatformKey = (typeof PLATFORMS)[number]['key'];

type SelectPlatformsDialogProps = {
    open: boolean;
    onClose: () => void;
    hasAmazonDspEntities: boolean;
    hasDv360Entities: boolean;
    selectedPlatforms: Set<PlatformKey>;
    onConfirm: (platforms: Set<PlatformKey>) => void;
};

export default function SelectPlatformsDialog({
    open,
    onClose,
    hasAmazonDspEntities,
    hasDv360Entities,
    selectedPlatforms,
    onConfirm,
}: SelectPlatformsDialogProps) {
    const [amazon, setAmazon] = useState(false);
    const [dv360, setDv360] = useState(false);

    useEffect(() => {
        if (open) {
            setAmazon(selectedPlatforms.has('AMAZON'));
            setDv360(selectedPlatforms.has('DV360'));
        }
    }, [open, selectedPlatforms]);

    function handleConfirm() {
        const next = new Set<PlatformKey>();
        if (amazon) next.add('AMAZON');
        if (dv360) next.add('DV360');
        onConfirm(next);
        onClose();
    }

    return (
        <Dialog fullWidth={true} maxWidth="xs" open={open} onClose={onClose}>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Select Platforms
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={amazon}
                            disabled={!hasAmazonDspEntities}
                            onChange={(e) => setAmazon(e.target.checked)}
                        />
                    }
                    label="Amazon DSP"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={dv360}
                            disabled={!hasDv360Entities}
                            onChange={(e) => setDv360(e.target.checked)}
                        />
                    }
                    label="Google DV360"
                />
                {(!hasAmazonDspEntities || !hasDv360Entities) && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        To enable unavailable platforms, complete the setup in DSP Integration Map.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="outlined" color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleConfirm}>
                    Confirm Selection
                </Button>
            </DialogActions>
        </Dialog>
    );
}
